/**
 * FlavorFeed — Attribution + Google Analytics 4 helpers
 * ======================================================
 *
 * Captures `?ref=<source>` and `?utm_*` params on first page load and
 * stashes them in localStorage so they survive any number of route
 * changes between landing and signup. Also exposes `trackEvent()` which
 * is a thin no-op wrapper around `window.gtag` — works whether or not
 * GA4 is actually loaded.
 *
 * GA4 itself is bootstrapped by `public/index.html` via the env var
 * `REACT_APP_GA4_ID` (e.g. `G-XXXXXXXXXX`). When that env var is unset
 * the gtag snippet skips itself, so this file is safe to call from
 * anywhere — it just becomes a logging-only path until you wire GA4.
 */

const STORAGE_KEY = 'ff_attribution';
const EXPIRY_DAYS = 30;

const _now = () => Date.now();

const _readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after EXPIRY_DAYS so a stale attribution from 6 months ago
    // doesn't get sent on a fresh signup.
    if (data.captured_at && (_now() - data.captured_at) > EXPIRY_DAYS * 24 * 3600 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

/**
 * Capture `?ref=<source>` and `?utm_*` params on page load. First-touch
 * wins — once a visitor has been tagged we don't overwrite (so a paid
 * Google Ad doesn't steal credit from the YouTube reel that brought
 * them first).
 *
 * Call this once at app bootstrap (App.js useEffect).
 */
export const captureAttributionFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const existing = _readStored();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });

  if (!ref && Object.keys(utm).length === 0) return null;

  const captured = {
    ref: ref || null,
    ...utm,
    landing_path: window.location.pathname,
    referrer: document.referrer || null,
    captured_at: _now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch { /* private-mode browsers: silent fail */ }
  return captured;
};

/** Read the stored attribution (or null if none). */
export const getAttribution = () => _readStored();

/** Clear after successful signup so the next signup gets a fresh slot. */
export const clearAttribution = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

/**
 * Fire a GA4 event. No-ops when gtag isn't on the page (env var unset
 * or ad-blocker). Never throws.
 *   trackEvent('signup_completed', { plan: 'free', ref: 'hero_reel' });
 */
export const trackEvent = (name, params = {}) => {
  try {
    const attribution = _readStored() || {};
    const enriched = { ...attribution, ...params };
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, enriched);
    }
    // Also log in dev so devs can see the event firing without GA4 open.
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[ga4]', name, enriched);
    }
  } catch { /* never break the app on analytics */ }
};

/** Convenience for the most common page-view event. */
export const trackPageView = (path) => trackEvent('page_view', { page_path: path || window.location.pathname });
